import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EditorModule } from '@tinymce/tinymce-angular';
import type { RawEditorOptions } from 'tinymce';
import {
  LucideAngularModule,
  X,
  Calendar,
  Clock,
  User,
  Eye,
  MapPin,
  DollarSign,
  FileText,
  Download,
} from 'lucide-angular';
import { ModalDataService } from '../../../../core/services/modal-data.service';
import { DocumentService } from '../../../../core/services/document.service';
import { ModalService } from '../../../../core/services/modal.service';
import { PrestationService } from '../../../../core/services/prestation.service';
import { ToastService } from '../../../../core/services/toast.service';
import { DocumentViewerModalComponent } from '../../../../shared/components/modal/document-viewer-modal';
import {
  Prestation,
  PrestationDocument,
} from '../../../../core/interfaces/prestation.interface';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { environment } from '../../../../../environments/environment';

type MedicalActItem = {
  libelle: string;
  dateActe: string;
  effectuePar: string;
  statut: string;
  preComment?: string;
  postComment?: string;
  actTime?: string;
};

@Component({
  selector: 'app-prestation-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EditorModule,
    MatTooltipModule,
    MatMenuModule,
  ],
  templateUrl: './prestation-details-modal.component.html',
  styleUrls: ['./prestation-details-modal.component.scss'],
})
export class PrestationDetailsModalComponent implements OnInit {
  readonly useMockData = environment.useMockData ?? !environment.production;
  private readonly mockOnlySectionKeys = [
    'motifs',
    'actes_medicaux',
    'diagnostic',
    'conclusion',
    'formulaires_documents',
  ];

  readonly X = X;
  readonly Calendar = Calendar;
  readonly Clock = Clock;
  readonly User = User;
  readonly MapPin = MapPin;
  readonly DollarSign = DollarSign;
  readonly FileText = FileText;
  readonly Download = Download;
  readonly Eye = Eye;

  prestation: Prestation | null = null;
  documents: PrestationDocument[] = [];
  loading = true;
  loadingDocuments = true;
  isPrestationMedical: boolean | null = null;
  tinyMceConfig: RawEditorOptions = {
    menubar: false,
    statusbar: false,
    plugins: 'lists link',
    toolbar:
      'undo redo | bold italic underline | bullist numlist | alignleft aligncenter alignright',
    height: 180,
    min_height: 140,
    toolbar_mode: 'sliding',
    resize: false,
    content_style:
      'body { font-family: Poppins, sans-serif; font-size: 14px; line-height: 1.5; } p { margin: 0 0 8px; }',
    branding: false,
    base_url: '/tinymce',
    suffix: '.min',
    license_key: 'gpl',
  };

  // Propriétés pour les accordéons
  openAccordion: string | null = null; // Un seul accordéon ouvert à la fois
  accordionSections = [
    { key: 'motifs', label: 'MOTIF' },
    { key: 'histoire_maladie', label: 'HISTOIRE DE LA MALADIE' },
    { key: 'actes_medicaux', label: 'ACTES MEDICAUX' },
    { key: 'diagnostic', label: 'DIAGNOSTIC' },
    { key: 'ordonnances', label: 'ORDONNANCES' },
    { key: 'examens_demandes', label: 'EXAMENS DEMANDÉS' },
    { key: 'conclusion', label: 'CONCLUSION' },
    { key: 'formulaires_documents', label: 'FORMULAIRES / DOCUMENTS' },
  ];

  get visibleAccordionSections(): Array<{ key: string; label: string }> {
    if (this.useMockData) {
      return this.accordionSections;
    }
    return this.accordionSections.filter(
      (section) => !this.mockOnlySectionKeys.includes(section.key),
    );
  }

  // Données récupérées de l'API
  apiData: any = {
    actes_medicaux: null,
    diagnostic: null,
    motifs: null,
    formulaires_documents: null,
    histoire_maladie: null,
    conclusion: null,
    ordonnances: null,
    examens_demandes: null,
  };

  // États de chargement par section
  loadingSections: any = {
    actes_medicaux: false,
    diagnostic: false,
    motifs: false,
    formulaires_documents: false,
    histoire_maladie: false,
    conclusion: false,
    ordonnances: false,
    examens_demandes: false,
  };

  siMockData = {
    actes_medicaux: {
      items: [] as MedicalActItem[],
      pageSize: 10,
    },
    diagnostic: {
      maladieEtatSanteCode: '',
      commentaireHtml: '',
      options: [] as Array<{ code: string; label: string }>,
    },
    motifs: {
      motifCode: '',
      commentaireHtml: '',
      options: [] as Array<{ code: string; label: string }>,
    },
    formulaires_documents: {
      forms: [] as Array<{ nom: string; extension: string }>,
      documents: [] as Array<{ nom: string; extension: string }>,
      pageSize: 10,
    },
    histoire_maladie: {
      contenuHtml: '',
    },
    conclusion: {
      contenuHtml: '',
    },
  };

  isCreateActPanelOpen = false;
  isMedicalActDetailsPanelOpen = false;
  selectedMedicalAct: MedicalActItem | null = null;
  createActSubmitted = false;
  createActForm = {
    actCode: '',
    practitioner: '',
    status: 'Effectue(e)',
    preComment: '',
    postComment: '',
    actDate: '2026-04-08',
    actTime: '13:09',
  };
  medicalActOptions = [
    { code: 'consult_orl', label: 'Consultation specialisee ORL' },
    { code: 'nebulisation', label: 'Nebulisation' },
    { code: 'pansement', label: 'Pansement' },
  ];
  practitionerOptions = [
    { id: 'dr_diallo', label: 'Dr. Diallo' },
    { id: 'dr_konate', label: 'Dr. Konate' },
    { id: 'inf_traore', label: 'Infirmier Traore' },
  ];
  isCreateDocumentPanelOpen = false;
  createDocumentSubmitted = false;
  createDocumentForm = {
    documentType: '',
  };
  selectedDocumentFile: File | null = null;
  documentTypeOptions = [
    { value: 'compte-rendu', label: 'Compte-rendu' },
    { value: 'resultat-analyse', label: "Résultat d'analyse" },
    { value: 'ordonnance', label: 'Ordonnance' },
    { value: 'imagerie', label: 'Imagerie médicale' },
    { value: 'autre', label: 'Autre' },
  ];

  constructor(
    private documentService: DocumentService,
    private modalService: ModalService,
    private prestationService: PrestationService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    // Récupérer les données de la prestation depuis le service
    const data = ModalDataService.getData();
    if (data && data.prestation) {
      this.prestation = data.prestation;
      console.log('PrestationDetailsModal: Prestation reçue:', this.prestation);

      this.isPrestationMedical = this.prestation?.category?.code === 'MEDICAL';
      // Debug des medicalQuoteItems
      if (
        this.prestation?.medicalQuoteItems &&
        this.prestation.medicalQuoteItems.length > 0
      ) {
        console.log(
          'PrestationDetailsModal: MedicalQuoteItems trouvés:',
          this.prestation.medicalQuoteItems,
        );
      } else {
        console.log('PrestationDetailsModal: Aucun medicalQuoteItem trouvé');
      }

      // Récupérer les détails complets de la prestation via l'API
      if (this.prestation?._id) {
        this.prestationService
          .getPrestationById(this.prestation._id)
          .subscribe({
            next: (prestationDetails) => {
              if (prestationDetails) {
                console.log(
                  'PrestationDetailsModal: Détails de la prestation récupérés:',
                  prestationDetails,
                );
                this.prestation = prestationDetails;
                this.isPrestationMedical =
                  this.prestation?.category?.code === 'MEDICAL';
              }
            },
            error: (error) => {
              console.error(
                'PrestationDetailsModal: Erreur lors de la récupération des détails:',
                error,
              );
            },
          });

        // Récupérer les documents de la prestation via l'API
        this.loadPrestationDocuments(this.prestation._id);
      } else {
        this.loadingDocuments = false;
      }
    } else {
      console.error('PrestationDetailsModal: Aucune prestation reçue');
      this.loadingDocuments = false;
    }
    this.loading = false;
  }

  loadPrestationDocuments(prestationId: string): void {
    this.loadingDocuments = true;
    this.documentService.getPrestationDocuments(prestationId).subscribe({
      next: (response) => {
        console.log('PrestationDetailsModal: Documents récupérés:', response);
        this.documents = response.documents || [];
      },
      error: (error) => {
        console.error(
          'PrestationDetailsModal: Erreur lors du chargement des documents:',
          error,
        );
        this.documents = [];
        this.loadingDocuments = false;
      },
      complete: () => {
        this.loadingDocuments = false;
      },
    });
  }

  getSourceLabel(source: string): string {
    const sourceMap: Record<string, string> = {
      BILLING: 'Facturation',
      PATIENT_FILE: 'Dossier patient',
      INSURANCE: 'Assurance',
      MANUAL: 'Saisie manuelle',
      AUTOMATIC: 'Automatique',
      IMPORT: 'Import',
      API: 'API',
      OTHER: 'Autre',
    };
    return sourceMap[source] || source;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatAmount(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
    }).format(amount);
  }

  closeModal(): void {
    // Fermer le modal en utilisant le ModalService
    this.modalService.closeAll();
  }

  viewDocument(document: any): void {
    console.log('PrestationDetailsModal: Affichage du document:', document);

    if (!document.url) {
      console.error('PrestationDetailsModal: URL du document manquante');
      return;
    }

    // Toujours obtenir une URL presignée, même pour les URLs complètes
    // car elles nécessitent une authentification
    this.documentService.getPresignedUrl(document.url).subscribe({
      next: (response) => {
        console.log(
          'PrestationDetailsModal: URL presignée obtenue:',
          response.url,
        );

        window.open(response.url, '_blank');
      },
      error: (error) => {
        console.error(
          "PrestationDetailsModal: Erreur lors de l'obtention de l'URL presignée:",
          error,
        );
        this.toastService.error('Erreur lors du chargement du document');
      },
    });
  }

  downloadDocument(document: any): void {
    console.log(
      'PrestationDetailsModal: Téléchargement du document:',
      document,
    );

    if (!document.url) {
      console.error('PrestationDetailsModal: URL du document manquante');
      return;
    }

    // Toujours obtenir une URL presignée, même pour les URLs complètes
    // car elles nécessitent une authentification
    this.documentService.getPresignedUrl(document.url).subscribe({
      next: (response) => {
        console.log(
          'PrestationDetailsModal: URL presignée obtenue:',
          response.url,
        );

        // Télécharger le fichier en utilisant fetch pour forcer le téléchargement
        this.downloadFile(response.url, document.filename || 'document');
      },
      error: (error) => {
        console.error(
          "PrestationDetailsModal: Erreur lors de l'obtention de l'URL presignée:",
          error,
        );
        this.toastService.error('Erreur lors du chargement du document');
      },
    });
  }

  private downloadFile(url: string, filename: string) {
    // Afficher un indicateur de chargement
    this.toastService.info('Téléchargement en cours...');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        // Créer un blob URL pour le téléchargement
        const blobUrl = window.URL.createObjectURL(blob);

        // Créer un lien temporaire pour télécharger le document
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';

        // Ajouter le lien au DOM, cliquer dessus, puis le supprimer
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Nettoyer le blob URL
        window.URL.revokeObjectURL(blobUrl);

        // Afficher un message de succès
        this.toastService.success('Document téléchargé avec succès !');
      })
      .catch((error) => {
        console.error(
          'PrestationDetailsModal: Erreur lors du téléchargement:',
          error,
        );
        this.toastService.error('Erreur lors du téléchargement du document');
      });
  }

  trackByStay(index: number, stay: any): string {
    // Utiliser l'index et les dates pour créer une clé unique
    return `stay-${index}-${stay.startDate}-${stay.endDate}`;
  }

  trackByDocument(index: number, document: PrestationDocument): string {
    // Utiliser l'index et l'ID ou le nom pour créer une clé unique
    const id = document._id || document.filename || `doc-${index}`;
    return `document-${index}-${id}`;
  }

  getQuoteItemStatusClass(status: any): string {
    if (status.done) return 'bg-green-100 text-green-800';
    if (status.scheduled) return 'bg-blue-100 text-blue-800';
    if (status.onGoing) return 'bg-yellow-100 text-yellow-800';
    if (status.cancelled) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }

  getPaymentStatusClass(paymentStatus: any): string {
    const statusMap: Record<string, string> = {
      TO_PAYED: 'bg-red-100 text-red-800',
      PAYED: 'bg-green-100 text-green-800',
      PARTIALLY_PAYED: 'bg-yellow-100 text-yellow-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
    };
    return statusMap[paymentStatus.code] || 'bg-gray-100 text-gray-800';
  }

  trackByQuoteItem(index: number, item: any): string {
    return `quote-item-${index}-${item.identifier}`;
  }

  // Méthodes pour les accordéons
  toggleAccordion(sectionKey: string): void {
    if (this.openAccordion === sectionKey) {
      this.openAccordion = null; // Fermer si déjà ouvert
    } else {
      this.openAccordion = sectionKey; // Ouvrir le nouvel accordéon

      // Charger les données depuis l'API si pas encore chargées
      if (!this.apiData[sectionKey] && this.prestation?._id) {
        this.loadSectionData(sectionKey);
      }
    }
  }

  /**
   * Charge les données d'une section depuis l'API
   */
  loadSectionData(sectionKey: string): void {
    if (!this.prestation?._id) {
      console.error('PrestationDetailsModal: ID de prestation manquant');
      return;
    }

    this.loadingSections[sectionKey] = true;

    if (sectionKey === 'ordonnances') {
      // Charger les ordonnances
      this.prestationService
        .getPrestationPrescriptions(this.prestation._id)
        .subscribe({
          next: (data) => {
            console.log(
              'PrestationDetailsModal: Ordonnances récupérées:',
              data,
            );
            this.apiData.ordonnances = data;
            this.loadingSections.ordonnances = false;
          },
          error: (error) => {
            console.error(
              'PrestationDetailsModal: Erreur lors du chargement des ordonnances:',
              error,
            );
            this.loadingSections.ordonnances = false;
          },
        });
    } else if (sectionKey === 'examens_demandes') {
      // Charger les examens demandés
      this.prestationService
        .getPrestationRequestedExams(this.prestation._id)
        .subscribe({
          next: (data) => {
            console.log(
              'PrestationDetailsModal: Examens demandés récupérés:',
              data,
            );
            this.apiData.examens_demandes = data;
            this.loadingSections.examens_demandes = false;
          },
          error: (error) => {
            console.error(
              'PrestationDetailsModal: Erreur lors du chargement des examens demandés:',
              error,
            );
            this.loadingSections.examens_demandes = false;
          },
        });
    } else {
      // Pour documents_formulaires, on garde les données mock pour l'instant
      this.loadingSections[sectionKey] = false;
    }
  }

  isAccordionOpen(sectionKey: string): boolean {
    return this.openAccordion === sectionKey;
  }

  isMockSiSection(sectionKey: string): boolean {
    return this.useMockData && this.mockOnlySectionKeys.includes(sectionKey);
  }

  saveMockSection(sectionKey: string): void {
    const section = this.accordionSections.find((s) => s.key === sectionKey);
    this.toastService.success(
      `"${section?.label ?? sectionKey}" sauvegarde localement (mock).`,
    );
  }

  openCreateMedicalActPanel(): void {
    this.isCreateActPanelOpen = true;
  }

  closeCreateMedicalActPanel(): void {
    this.isCreateActPanelOpen = false;
    this.createActSubmitted = false;
  }

  isCreateActFormInvalid(): boolean {
    return !this.createActForm.actCode || !this.createActForm.practitioner;
  }

  saveCreateMedicalAct(): void {
    this.createActSubmitted = true;
    if (this.isCreateActFormInvalid()) {
      return;
    }

    const selectedAct = this.medicalActOptions.find(
      (a) => a.code === this.createActForm.actCode,
    );
    const selectedPractitioner = this.practitionerOptions.find(
      (p) => p.id === this.createActForm.practitioner,
    );

    this.siMockData.actes_medicaux.items.unshift({
      libelle: selectedAct?.label ?? this.createActForm.actCode,
      dateActe: this.formatDate(this.createActForm.actDate),
      effectuePar:
        selectedPractitioner?.label ?? this.createActForm.practitioner,
      statut: this.createActForm.status,
      preComment: this.createActForm.preComment,
      postComment: this.createActForm.postComment,
      actTime: this.createActForm.actTime,
    });

    this.toastService.success('Acte medical ajoute (mock).');
    this.closeCreateMedicalActPanel();
  }

  onMedicalActDetails(item: MedicalActItem): void {
    this.selectedMedicalAct = item;
    this.isMedicalActDetailsPanelOpen = true;
  }

  closeMedicalActDetailsPanel(): void {
    this.isMedicalActDetailsPanelOpen = false;
    this.selectedMedicalAct = null;
  }

  onMedicalActChangeStatus(index: number): void {
    const item = this.siMockData.actes_medicaux.items[index];
    if (!item) return;
    const statusOrder = ['Planifie', 'Effectue(e)', 'Termine', 'Annule'];
    const currentIndex = statusOrder.indexOf(item.statut);
    const nextIndex = (currentIndex + 1) % statusOrder.length;
    item.statut = statusOrder[nextIndex];
    this.toastService.success(`Statut mis à jour: ${item.statut}`);
  }

  onMedicalActEdit(index: number): void {
    const item = this.siMockData.actes_medicaux.items[index];
    if (!item) return;
    this.createActForm = {
      ...this.createActForm,
      actCode:
        this.medicalActOptions.find((opt) => opt.label === item.libelle)
          ?.code ?? '',
      practitioner:
        this.practitionerOptions.find((opt) => opt.label === item.effectuePar)
          ?.id ?? '',
      status: item.statut,
      actDate: '2026-04-08',
      actTime: '13:09',
    };
    this.openCreateMedicalActPanel();
    this.toastService.info('Mode édition (mock) ouvert.');
  }

  onMedicalActDelete(index: number): void {
    const item = this.siMockData.actes_medicaux.items[index];
    if (!item) return;
    this.siMockData.actes_medicaux.items.splice(index, 1);
    this.toastService.success(`Acte supprimé: ${item.libelle}`);
  }

  openCreateDocumentPanel(): void {
    this.isCreateDocumentPanelOpen = true;
  }

  closeCreateDocumentPanel(): void {
    this.isCreateDocumentPanelOpen = false;
    this.createDocumentSubmitted = false;
    this.createDocumentForm = { documentType: '' };
    this.selectedDocumentFile = null;
  }

  onCreateDocumentFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedDocumentFile = file;
  }

  isCreateDocumentFormInvalid(): boolean {
    return !this.createDocumentForm.documentType || !this.selectedDocumentFile;
  }

  saveCreateDocument(): void {
    this.createDocumentSubmitted = true;
    if (this.isCreateDocumentFormInvalid()) {
      return;
    }

    const typeLabel =
      this.documentTypeOptions.find(
        (item) => item.value === this.createDocumentForm.documentType,
      )?.label ?? this.createDocumentForm.documentType;
    const fileName = this.selectedDocumentFile?.name ?? 'document';
    const extension = fileName.includes('.')
      ? (fileName.split('.').pop()?.toUpperCase() ?? '')
      : '';

    this.siMockData.formulaires_documents.documents.unshift({
      nom: `${typeLabel} - ${fileName}`,
      extension: extension || 'FILE',
    });

    this.toastService.success('Document ajouté (mock).');
    this.closeCreateDocumentPanel();
  }

  getSectionDocuments(sectionKey: string): any[] {
    // Utiliser les données de l'API si disponibles, sinon les données mock
    if (this.apiData[sectionKey]) {
      // Adapter selon la structure de la réponse API
      if (sectionKey === 'examens_demandes') {
        // Pour les examens demandés, la réponse contient un objet avec requestedExams
        return this.apiData[sectionKey]?.requestedExams || [];
      }
      // Pour les ordonnances, c'est directement un tableau
      return Array.isArray(this.apiData[sectionKey])
        ? this.apiData[sectionKey]
        : this.apiData[sectionKey]?.data ||
            this.apiData[sectionKey]?.items ||
            [];
    }
    return [];
  }

  /**
   * Retourne le nombre de documents pour une section
   * Retourne null si les données ne sont pas encore chargées depuis l'API
   */
  getSectionDocumentsCount(sectionKey: string): number | null {
    // Si les données de l'API sont disponibles, retourner le nombre réel
    if (
      this.apiData[sectionKey] !== null &&
      this.apiData[sectionKey] !== undefined
    ) {
      const documents = this.getSectionDocuments(sectionKey);
      return documents.length;
    }
    // Si on est en train de charger, retourner null pour ne pas afficher le nombre mock
    if (this.loadingSections[sectionKey]) {
      return null;
    }
    // Si pas encore chargé, retourner null pour ne pas afficher le nombre mock
    return null;
  }

  viewMockDocument(document: any): void {
    console.log('Visualisation du document mock:', document);
    // TODO: Implémenter la visualisation
  }

  downloadMockDocument(document: any): void {
    console.log('Téléchargement du document mock:', document);
    // TODO: Implémenter le téléchargement
  }

  getDiagnosticOptionLabel(): string {
    const selectedOption = this.siMockData.diagnostic.options.find(
      (option) =>
        option.code === this.siMockData.diagnostic.maladieEtatSanteCode,
    );
    return selectedOption?.label || '-';
  }

  getMotifOptionLabel(): string {
    const selectedOption = this.siMockData.motifs.options.find(
      (option) => option.code === this.siMockData.motifs.motifCode,
    );
    return selectedOption?.label || '-';
  }

  /**
   * Formate la date de création d'une ordonnance
   */
  formatPrescriptionDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return dateString; // Retourner la chaîne originale si le parsing échoue
    }
  }

  /**
   * Retourne le titre approprié selon le type de prestation
   * @returns "Fiches médicales" pour les prestations médicales, "Documents" pour les autres
   */
  getDocumentsSectionTitle(): string {
    return this.isPrestationMedical ? 'Fiche médicale' : 'Documents';
  }
}
